import React, { useState, useEffect, useRef } from 'react';
import { Settings as SettingsIcon, Users, MessageSquare, Save, ChevronDown, Layout } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import Container from '../components/Container';
import FullScreenLoader from '../components/Loader';

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [accountList, setAccountList] = useState([]);
  const [default_accounts, setdefault_accounts] = useState([]);
  const [splitMessage, setSplitMessage] = useState(50);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const accRes = await api.get('/whatsapp/checkStatus');
      setAccountList(accRes.data?.data || []);

      const settingsRes = await api.get('/settings/get-settings');
      if (settingsRes.data) {
        let default_accout = [...JSON.parse(settingsRes?.data?.default_accounts || '[]')];
        setdefault_accounts(default_accout);
        setSplitMessage(settingsRes.data.split_value || 50);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const toggleAccount = (phone) => {
    let oldState = [...default_accounts];

    oldState = oldState?.includes(phone) ? oldState?.filter(id => id !== phone) : [...oldState, phone]
    setdefault_accounts(oldState);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.post('/settings/save-settings', {
        default_accounts,
        split_value: parseInt(splitMessage)
      });
      toast.success("Configuration saved successfully!");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container title={'Global Settings'}>
      {loading && <FullScreenLoader />}
      <div style={localStyles.fullPageWrapper}>
        <div style={localStyles.card}>
          {/* Header Section */}
          <div style={localStyles.header}>
            <div style={localStyles.iconCircle}>
              <SettingsIcon size={24} color="#4f46e5" />
            </div>
            <div>
              <h2 style={localStyles.title}>System Configuration</h2>
              <p style={localStyles.subtitle}>Manage broadcast limits and default sender accounts</p>
            </div>
          </div>

          <hr style={localStyles.divider} />

          {/* Accounts Selection */}
          <div style={localStyles.formGroup}>
            <label style={localStyles.label}>
              <Users size={16} color="#64748b" /> Default Sending Accounts
            </label>
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <div style={localStyles.multiSelectTrigger} onClick={() => setShowAccountDropdown(!showAccountDropdown)}>
                <span style={{ fontSize: '14px', color: default_accounts?.length > 0 ? '#1e293b' : '#94a3b8' }}>
                  {default_accounts?.length > 0 
                    ? `${default_accounts?.length} Accounts Selected` 
                    : "Choose default accounts..."}
                </span>
                <ChevronDown size={18} color="#64748b" />
              </div>

              {showAccountDropdown && (
                <div style={localStyles.dropdownList}>
                  {accountList.length > 0 ? accountList.map(acc => (
                    <div key={acc.phone} style={localStyles.option} onClick={() => toggleAccount(acc.phone)}>
                      <input 
                        type="checkbox" 
                        checked={default_accounts?.includes(acc.phone)} 
                        readOnly 
                        style={localStyles.checkbox}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '13px' }}>{acc.name || 'WhatsApp Account'}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{acc.phone}</div>
                      </div>
                    </div>
                  )) : (
                    <div style={{ padding: '15px', textAlign: 'center', fontSize: '12px', color: '#94a3b8' }}>
                      No active accounts found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Split Message Input */}
          <div style={localStyles.formGroup}>
            <label style={localStyles.label}>
              <MessageSquare size={16} color="#64748b" /> Split Message Threshold
            </label>
            <div style={localStyles.inputWrapper}>
              <input 
                type="number" 
                style={localStyles.input} 
                value={splitMessage}
                onChange={(e) => setSplitMessage(e.target.value)}
                placeholder="50"
              />
              <div style={localStyles.inputBadge}>Messages</div>
            </div>
            <small style={localStyles.helpText}>
              System will switch to the next account after sending these many messages.
            </small>
          </div>

          {/* Save Button */}
          <button style={localStyles.saveBtn} onClick={handleSave} disabled={loading}>
            <Save size={18} /> 
            {loading ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </div>
    </Container>
  );
};

const localStyles = {
  fullPageWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '40px',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)',
    width: '70%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '10px'
  },
  iconCircle: {
    backgroundColor: '#f5f3ff',
    padding: '12px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  title: {
    fontSize: '20px',
    fontWeight: '800',
    color: '#1e293b',
    margin: 0
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748b',
    margin: '2px 0 0 0'
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #f1f5f9',
    margin: '25px 0'
  },
  formGroup: {
    marginBottom: '25px',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#475569',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  multiSelectTrigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    borderRadius: '14px',
    border: '1.5px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  dropdownList: {
    position: 'absolute',
    top: '105%',
    left: 0,
    right: 0,
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
    zIndex: 100,
    maxHeight: '220px',
    overflowY: 'auto',
    padding: '8px'
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'background 0.2s',
    marginBottom: '2px',
    '&:hover': { backgroundColor: '#f1f5f9' }
  },
  checkbox: {
    width: '16px',
    height: '16px',
    accentColor: '#4f46e5',
    cursor: 'pointer'
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  input: {
    width: '100%',
    padding: '14px 18px',
    borderRadius: '14px',
    border: '1.5px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    fontSize: '15px',
    fontWeight: '600',
    outline: 'none',
    transition: 'border-color 0.2s',
    '&:focus': { borderColor: '#4f46e5' }
  },
  inputBadge: {
    position: 'absolute',
    right: '15px',
    backgroundColor: '#e2e8f0',
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: '700',
    color: '#64748b'
  },
  helpText: {
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '8px',
    display: 'block',
    lineHeight: '1.4'
  },
  saveBtn: {
    width: '100%',
    marginTop: '10px',
    backgroundColor: '#4f46e5',
    color: 'white',
    padding: '16px',
    borderRadius: '16px',
    border: 'none',
    fontWeight: '700',
    fontSize: '15px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
    transition: 'transform 0.2s, opacity 0.2s',
    '&:active': { transform: 'scale(0.98)' },
    '&:disabled': { opacity: 0.7, cursor: 'not-allowed' }
  }
};

export default Settings;